import dayjs from "dayjs";
import dynamic from "next/dynamic";
import React, { useMemo } from "react";
import uPlot from "uplot";

import { HistoricalRatingData } from "@/features/analysis/historical-rating/services/historical-rating";
import { useLang } from "@/hooks/use-lang";
import { DateOption, getFirstDate } from "@/utils/date-options";

const UAreaLineChart = dynamic(
  () =>
    import("ui/analysis/u-area-line-chart").then((mod) => mod.UAreaLineChart),
  { ssr: false }
);

type Props = {
  historicalRatings: HistoricalRatingData[];
  dateOption: DateOption;
};

const HistoricalRatingChart = ({ historicalRatings, dateOption }: Props) => {
  const { t, lang } = useLang();

  const startIndex = useMemo(() => {
    const firstDate = dayjs(getFirstDate(dateOption));
    return (
      historicalRatings.findIndex(
        ({ date }) => firstDate.isSame(date) || firstDate.isBefore(date)
      ) ?? 0
    );
  }, [dateOption, historicalRatings]);

  const [data, lowerBound, upperBound] = useMemo(
    () => prepareChartData(historicalRatings, startIndex),
    [historicalRatings, startIndex]
  );

  return (
    <div className="flex w-full flex-col pb-6 @lg:pb-8">
      <div className="h-52 w-[105%] -translate-x-2 @lg:h-64 @lg:w-full @lg:translate-x-0">
        <UAreaLineChart
          data={data}
          maxY={upperBound}
          minY={lowerBound}
          lang={lang}
          dateLabel={t.analysis.shared.date}
          label={t.analysis.shared.price}
          showLegend="static"
          showTooltip
          valueFormatter={(value) => `$${value}`}
          strokeColor="#3382B5"
          strokeWidth={2}
          areaLabels={[
            t.analysis.shared.scoreOption.getScore(1),
            t.analysis.shared.scoreOption.getScore(2),
            t.analysis.shared.scoreOption.getScore(3),
            t.analysis.shared.scoreOption.getScore(4),
            t.analysis.shared.scoreOption.getScore(5),
          ]}
        />
      </div>
    </div>
  );
};

const prepareChartData = (
  historicalRatings?: HistoricalRatingData[],
  startIndex?: number
): [uPlot.AlignedData, number, number] => {
  if (!historicalRatings || historicalRatings.length === 0) return [[], 0, 0];

  const slicedHistoricalRatings = historicalRatings.slice(startIndex);
  let maxValue = slicedHistoricalRatings[0].price;
  let minValue = slicedHistoricalRatings[0].price;

  for (const { price } of slicedHistoricalRatings) {
    if (price > maxValue) maxValue = price;
    else if (price < minValue) minValue = price;
  }

  const upperBound = Math.ceil(maxValue * 1.1);
  const lowerBound = Math.floor(minValue * 0.9);

  const dates: number[] = [];
  // ! 需要在第一個資料放入 upperBound，才能正確顯示 area
  const score1: (number | null)[] = [upperBound];
  const score2: (number | null)[] = [upperBound];
  const score3: (number | null)[] = [upperBound];
  const score4: (number | null)[] = [upperBound];
  const score5: (number | null)[] = [upperBound];
  const prices: number[] = [];
  const scores: number[] = [];

  for (const { date, price, rating = 0 } of slicedHistoricalRatings) {
    dates.push(dayjs(date).unix());
    prices.push(+price?.toFixed(2));
    scores.push(rating);
    score1.push(rating === 1 ? upperBound : null);
    score2.push(rating === 2 ? upperBound : null);
    score3.push(rating === 3 ? upperBound : null);
    score4.push(rating === 4 ? upperBound : null);
    score5.push(rating === 5 ? upperBound : null);
  }

  return [
    [dates, score1, score2, score3, score4, score5, prices, scores],
    lowerBound,
    upperBound,
  ];
};

const MemoHistoricalRatingChart = React.memo(HistoricalRatingChart);
export { MemoHistoricalRatingChart };
